/**
 * TermsCard — Settings UI for Pillar #1 (Semester model).
 *
 * Add/edit/delete terms. Powers cross-semester transcript + finals-week
 * detection. Backend: GET/POST/PUT/DELETE /api/terms.
 */
import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Card } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Calendar, Plus, Trash2, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import api, { apiUrl } from '../utils/apiClient';

const today = () => new Date().toISOString().slice(0, 10);
const inMonths = (n) => {
  const d = new Date();
  d.setMonth(d.getMonth() + n);
  return d.toISOString().slice(0, 10);
};

export default function TermsCard() {
  const [terms, setTerms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [draft, setDraft] = useState({ name: '', start_date: today(), end_date: inMonths(4), target_gpa: 3.5 });
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get(apiUrl('/terms'));
      setTerms(data.terms || []);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const create = async () => {
    if (!draft.name.trim()) return toast.error('Term name required');
    setSaving(true);
    try {
      await api.post(apiUrl('/terms'), { ...draft, subject_ids: [] });
      setDraft({ name: '', start_date: today(), end_date: inMonths(4), target_gpa: 3.5 });
      await load();
      toast.success('Term added');
    } catch { toast.error('Could not add'); }
    finally { setSaving(false); }
  };

  const remove = async (id) => {
    if (!confirm('Delete this term?')) return;
    try {
      await api.delete(apiUrl(`/terms/${id}`));
      await load();
      toast.success('Removed');
    } catch { toast.error('Could not remove'); }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.3 }}
      data-testid="terms-card"
    >
      <Card className="f500-card p-6 lg:p-8">
        <div className="flex items-center gap-3 pb-4 border-b border-[var(--isp-glass-border)] mb-4">
          <Calendar className="w-5 h-5 text-[var(--isp-heading)]" strokeWidth={1.75} />
          <div>
            <h2 className="f500-h2 text-xl">Academic Terms</h2>
            <p className="text-sm text-[var(--isp-body)] mt-1">
              Tell us when your semesters start and end. Powers finals-week mode + cross-semester transcript.
            </p>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center gap-2 text-[var(--isp-body)]"><Loader2 className="w-4 h-4 animate-spin" /> Loading…</div>
        ) : (
          <>
            {terms.length > 0 && (
              <div className="border border-[var(--isp-glass-border)] mb-4">
                {terms.map((t) => (
                  <div key={t.id} className="grid grid-cols-[1.5fr_auto_auto] gap-3 px-4 py-3 items-center border-b last:border-b-0 border-[var(--isp-glass-border)]" data-testid={`term-row-${t.id}`}>
                    <div>
                      <p className="text-sm font-semibold text-[var(--isp-heading)]" style={{ fontFamily: 'var(--sf-text)' }}>{t.name}</p>
                      <p className="text-xs text-[var(--isp-muted)]">{t.start_date} → {t.end_date}{t.target_gpa ? ` · target GPA ${t.target_gpa}` : ''}</p>
                    </div>
                    <button onClick={() => remove(t.id)} className="p-1.5 text-[var(--isp-muted)] hover:text-red-500 transition-colors" title="Delete" data-testid={`term-delete-${t.id}`}>
                      <Trash2 className="w-3.5 h-3.5" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            <div className="border border-[var(--isp-glass-border)] p-4 bg-[var(--isp-glass-inner)]">
              <div className="grid grid-cols-1 sm:grid-cols-[1.4fr_1fr_1fr_0.7fr_auto] gap-3 items-end">
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--isp-muted)]">Term name</label>
                  <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="Fall 2026" className="mt-1 text-sm h-9" data-testid="term-add-name" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--isp-muted)]">Start</label>
                  <Input type="date" value={draft.start_date} onChange={(e) => setDraft({ ...draft, start_date: e.target.value })} className="mt-1 text-sm h-9" data-testid="term-add-start" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--isp-muted)]">End</label>
                  <Input type="date" value={draft.end_date} onChange={(e) => setDraft({ ...draft, end_date: e.target.value })} className="mt-1 text-sm h-9" data-testid="term-add-end" />
                </div>
                <div>
                  <label className="text-[10px] font-semibold uppercase tracking-[0.14em] text-[var(--isp-muted)]">Target GPA</label>
                  <Input type="number" step="0.1" min="0" max="4.0" value={draft.target_gpa} onChange={(e) => setDraft({ ...draft, target_gpa: parseFloat(e.target.value) || 0 })} className="mt-1 text-sm h-9" data-testid="term-add-gpa" />
                </div>
                <Button onClick={create} disabled={saving} className="bg-[#002FA7] hover:bg-[#00227A] text-white text-sm h-9" data-testid="term-add-submit">
                  <Plus className="w-3.5 h-3.5 mr-1" /> Add
                </Button>
              </div>
            </div>
          </>
        )}
      </Card>
    </motion.div>
  );
}
